import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Edit, Plus, Filter, Copy, Trash2, Save, DollarSign } from 'lucide-react';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../components/ui/use-toast';
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious
} from '../../components/ui/pagination';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

interface SalaryComponent {
  id: string;
  name: string;
  type: 'earning' | 'deduction';
  calculationType: 'percentage' | 'fixed';
  value: number;
  baseOn?: 'basic' | 'gross';  // For percentage calculations
  isEditable: boolean;
  isRequired: boolean;
  description?: string;
}

interface SalaryStructure {
  id: string;
  name: string;
  description: string;
  department: string;
  status: 'active' | 'draft' | 'archived';
  baseSalary: number;
  components: SalaryComponent[];
  createdBy?: string;
  createdDate?: string;
  lastModified?: string;
  assignedEmployees?: number;
}

// Predefined standard components
const standardComponents: SalaryComponent[] = [
  {
    id: 'basic',
    name: 'Basic Salary',
    type: 'earning',
    calculationType: 'percentage',
    value: 100,  // This will be calculated based on baseSalary
    isEditable: false,
    isRequired: true,
    description: 'Base component of the salary structure'
  },
  {
    id: 'hra',
    name: 'House Rent Allowance',
    type: 'earning',
    calculationType: 'percentage',
    value: 40,
    baseOn: 'basic',
    isEditable: true,
    isRequired: true,
    description: 'Allowance given to employees for housing expenses'
  },
  {
    id: 'da',
    name: 'Dearness Allowance',
    type: 'earning',
    calculationType: 'percentage',
    value: 10,
    baseOn: 'basic',
    isEditable: true,
    isRequired: false,
    description: 'Cost of living adjustment allowance'
  },
  {
    id: 'special',
    name: 'Special Allowance',
    type: 'earning',
    calculationType: 'percentage',
    value: 20,
    baseOn: 'basic',
    isEditable: true,
    isRequired: false,
    description: 'Additional allowance for special skills or requirements'
  },
  {
    id: 'pf',
    name: 'Provident Fund',
    type: 'deduction',
    calculationType: 'percentage',
    value: 12,
    baseOn: 'basic',
    isEditable: true,
    isRequired: true,
    description: 'Mandatory retirement savings contribution'
  },
  {
    id: 'tds',
    name: 'TDS (Tax Deducted at Source)',
    type: 'deduction',
    calculationType: 'percentage',
    value: 10,
    baseOn: 'gross',
    isEditable: true,
    isRequired: true,
    description: 'Income tax deducted from the salary'
  },
  {
    id: 'pt',
    name: 'Professional Tax',
    type: 'deduction',
    calculationType: 'fixed',
    value: 200,
    isEditable: true,
    isRequired: true,
    description: 'Tax imposed by state governments on professionals'
  }
];

// Generate salary structures
const generateSalaryStructures = (): SalaryStructure[] => {
  const structures: SalaryStructure[] = [];
  const departments = ['Faculty', 'Administration', 'Technical', 'Marketing', 'HR'];
  const statuses: ('active' | 'draft' | 'archived')[] = ['active', 'active', 'active', 'draft', 'archived'];
  
  departments.forEach((department, index) => {
    let baseSalary = 0;
    if (department === 'Faculty') {
      baseSalary = 60000;
    } else if (department === 'Administration') {
      baseSalary = 45000;
    } else if (department === 'Technical') {
      baseSalary = 55000;
    } else if (department === 'Marketing') {
      baseSalary = 50000;
    } else {
      baseSalary = 40000;
    }
    
    // Create date for "last modified"
    const lastModified = new Date();
    lastModified.setDate(lastModified.getDate() - Math.floor(Math.random() * 30));
    
    // Standard structure
    structures.push({
      id: `${index + 1}`,
      name: `${department} Standard`,
      description: `Standard salary structure for ${department} department employees.`,
      department,
      status: statuses[Math.floor(Math.random() * statuses.length)],
      baseSalary,
      components: [...standardComponents],
      createdBy: 'Admin User',
      createdDate: '2023-10-15',
      lastModified: lastModified.toISOString().split('T')[0],
      assignedEmployees: Math.floor(Math.random() * 10) + 5
    });
    
    // Senior structure
    structures.push({
      id: `${index + 6}`,
      name: `${department} Senior`,
      description: `Salary structure for senior ${department} department employees.`,
      department,
      status: statuses[Math.floor(Math.random() * statuses.length)],
      baseSalary: Math.round(baseSalary * 1.4),
      components: [
        ...standardComponents.map(comp => {
          // Slightly different percentages for senior positions
          if (comp.id === 'hra') {
            return { ...comp, value: 45 };
          } else if (comp.id === 'special') {
            return { ...comp, value: 25 };
          } else if (comp.id === 'tds') {
            return { ...comp, value: 15 };
          }
          return comp;
        })
      ],
      createdBy: 'Admin User',
      createdDate: '2023-10-15',
      lastModified: lastModified.toISOString().split('T')[0],
      assignedEmployees: Math.floor(Math.random() * 5) + 2
    });
  });
  
  return structures;
};

const SALARY_STRUCTURES = generateSalaryStructures();

const SalaryBreakdownCard = ({ structure }: { structure: SalaryStructure }) => {
  // Calculate totals and display the breakdown
  const calculateAmount = (component: SalaryComponent): number => {
    if (component.calculationType === 'fixed') {
      return component.value;
    } else if (component.calculationType === 'percentage') {
      if (component.id === 'basic') {
        return structure.baseSalary;
      }
      
      if (component.baseOn === 'basic') {
        const basicSalary = structure.baseSalary;
        return Math.round(basicSalary * (component.value / 100));
      } else {
        // Calculate gross salary for tds calculation
        const earningComponents = structure.components.filter(c => c.type === 'earning');
        const grossSalary = earningComponents.reduce((total, comp) => total + calculateAmount(comp), 0);
        return Math.round(grossSalary * (component.value / 100));
      }
    }
    return 0;
  };
  
  const earningComponents = structure.components.filter(c => c.type === 'earning');
  const deductionComponents = structure.components.filter(c => c.type === 'deduction');
  
  const totalEarnings = earningComponents.reduce((total, comp) => total + calculateAmount(comp), 0);
  const totalDeductions = deductionComponents.reduce((total, comp) => total + calculateAmount(comp), 0);
  const netSalary = totalEarnings - totalDeductions;
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium">Salary Breakdown</h3>
        <span className="text-xs font-medium bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
          CTC: ₹{totalEarnings.toLocaleString()}/month
        </span>
      </div>
      
      <div className="space-y-3">
        <div>
          <h4 className="text-xs font-medium text-gray-500 mb-2">EARNINGS</h4>
          {earningComponents.map(component => (
            <div key={component.id} className="flex justify-between text-sm py-1">
              <span className="text-gray-600">{component.name}</span>
              <span>₹{calculateAmount(component).toLocaleString()}</span>
            </div>
          ))}
          <div className="flex justify-between text-sm font-medium py-1 border-t mt-1 pt-1">
            <span>Total Earnings</span>
            <span>₹{totalEarnings.toLocaleString()}</span>
          </div>
        </div>
        
        <div>
          <h4 className="text-xs font-medium text-gray-500 mb-2">DEDUCTIONS</h4>
          {deductionComponents.map(component => (
            <div key={component.id} className="flex justify-between text-sm py-1">
              <span className="text-gray-600">{component.name}</span>
              <span>₹{calculateAmount(component).toLocaleString()}</span>
            </div>
          ))}
          <div className="flex justify-between text-sm font-medium py-1 border-t mt-1 pt-1">
            <span>Total Deductions</span>
            <span>₹{totalDeductions.toLocaleString()}</span>
          </div>
        </div>
      </div>
      
      <div className="flex justify-between font-medium text-lg pt-2 border-t">
        <span>Net Salary</span>
        <span className="text-bytesplash-blue">₹{netSalary.toLocaleString()}</span>
      </div>
    </div>
  );
};

const StructureEditorDialog = ({ 
  isOpen, 
  onClose, 
  structure, 
  onSave 
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  structure: SalaryStructure | null;
  onSave: (structure: SalaryStructure) => void;
}) => {
  const departments = ['Faculty', 'Administration', 'Technical', 'Marketing', 'HR'];
  const isEditMode = !!structure?.id;
  
  const [formData, setFormData] = useState<SalaryStructure>(
    structure || {
      id: '',
      name: '',
      description: '',
      department: '',
      status: 'draft',
      baseSalary: 50000,
      components: [...standardComponents]
    }
  );
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'baseSalary' ? parseInt(value) : value
    });
  };
  
  const handleSelectChange = (name: string, value: string) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleComponentChange = (id: string, field: string, value: any) => {
    setFormData({
      ...formData,
      components: formData.components.map(comp => {
        if (comp.id === id) {
          return { ...comp, [field]: value };
        }
        return comp;
      })
    });
  };
  
  const addNewComponent = (type: 'earning' | 'deduction') => {
    const newId = `custom_${Date.now()}`;
    const newComponent: SalaryComponent = {
      id: newId,
      name: type === 'earning' ? 'New Allowance' : 'New Deduction',
      type,
      calculationType: 'fixed',
      value: 0,
      isEditable: true,
      isRequired: false,
      description: ''
    };
    
    setFormData({
      ...formData,
      components: [...formData.components, newComponent]
    });
  };
  
  const removeComponent = (id: string) => {
    setFormData({
      ...formData,
      components: formData.components.filter(comp => comp.id !== id && comp.isRequired === false)
    });
  };
  
  const handleSave = () => {
    const currentDate = new Date().toISOString().split('T')[0];
    
    const updatedStructure: SalaryStructure = {
      ...formData,
      lastModified: currentDate,
      createdDate: formData.createdDate || currentDate,
      createdBy: formData.createdBy || 'Current User'
    };
    
    onSave(updatedStructure);
    onClose();
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? `Edit ${formData.name}` : 'Create New Salary Structure'}
          </DialogTitle>
          <DialogDescription>
            {isEditMode 
              ? 'Update the details of the salary structure'
              : 'Define a new salary structure with earnings and deductions'}
          </DialogDescription>
        </DialogHeader>
        
        <Tabs defaultValue="basic">
          <TabsList className="mb-6">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="earnings">Earnings</TabsTrigger>
            <TabsTrigger value="deductions">Deductions</TabsTrigger>
            <TabsTrigger value="preview">Preview</TabsTrigger>
          </TabsList>
          
          <TabsContent value="basic" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label htmlFor="name">Structure Name*</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter structure name"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="department">Department*</Label>
                <Select 
                  value={formData.department} 
                  onValueChange={(value) => handleSelectChange('department', value)}
                >
                  <SelectTrigger id="department">
                    <SelectValue placeholder="Select Department" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((dept) => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(value) => handleSelectChange('status', value as 'active' | 'draft' | 'archived')}
                >
                  <SelectTrigger id="status">
                    <SelectValue placeholder="Select Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2 col-span-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter a description for this salary structure"
                  rows={3}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="baseSalary">Base Salary (₹)*</Label>
                <Input
                  id="baseSalary"
                  name="baseSalary"
                  type="number"
                  value={formData.baseSalary}
                  onChange={handleChange}
                  required
                />
                <p className="text-xs text-gray-500">
                  This is the base amount used to calculate other components
                </p>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="earnings" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-base font-medium">Earnings Components</h3>
              <Button variant="outline" size="sm" onClick={() => addNewComponent('earning')}>
                <Plus className="h-3 w-3 mr-1" />
                Add Component
              </Button>
            </div>
            
            <div className="space-y-3">
              {formData.components.filter(comp => comp.type === 'earning').map((component) => (
                <div key={component.id} className="border rounded-md p-3 space-y-3">
                  <div className="flex justify-between items-center">
                    <div className="font-medium text-sm">
                      {component.name}
                      {component.isRequired && (
                        <span className="text-xs text-blue-600 ml-2">(Required)</span>
                      )}
                    </div>
                    
                    {!component.isRequired && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 w-6 p-0 text-red-500" 
                        onClick={() => removeComponent(component.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor={`${component.id}-name`} className="text-xs">Name</Label>
                      <Input
                        id={`${component.id}-name`}
                        value={component.name}
                        onChange={(e) => handleComponentChange(component.id, 'name', e.target.value)}
                        className="h-8 text-sm"
                        disabled={!component.isEditable}
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <Label htmlFor={`${component.id}-calcType`} className="text-xs">Calculation Type</Label>
                      <Select 
                        value={component.calculationType} 
                        onValueChange={(value) => handleComponentChange(component.id, 'calculationType', value as 'percentage' | 'fixed')}
                        disabled={!component.isEditable || component.id === 'basic'}
                      >
                        <SelectTrigger id={`${component.id}-calcType`} className="h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percentage">Percentage</SelectItem>
                          <SelectItem value="fixed">Fixed Amount</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-1">
                      <Label htmlFor={`${component.id}-value`} className="text-xs">
                        {component.calculationType === 'percentage' ? 'Percentage (%)' : 'Amount (₹)'}
                      </Label>
                      <Input
                        id={`${component.id}-value`}
                        type="number"
                        value={component.value}
                        onChange={(e) => handleComponentChange(component.id, 'value', parseFloat(e.target.value))}
                        className="h-8 text-sm"
                        disabled={!component.isEditable || component.id === 'basic'}
                      />
                    </div>
                    
                    {component.calculationType === 'percentage' && component.id !== 'basic' && (
                      <div className="space-y-1">
                        <Label htmlFor={`${component.id}-baseOn`} className="text-xs">Based On</Label>
                        <Select 
                          value={component.baseOn || 'basic'} 
                          onValueChange={(value) => handleComponentChange(component.id, 'baseOn', value as 'basic' | 'gross')}
                          disabled={!component.isEditable}
                        >
                          <SelectTrigger id={`${component.id}-baseOn`} className="h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="basic">Basic Salary</SelectItem>
                            <SelectItem value="gross">Gross Salary</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="deductions" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-base font-medium">Deductions Components</h3>
              <Button variant="outline" size="sm" onClick={() => addNewComponent('deduction')}>
                <Plus className="h-3 w-3 mr-1" />
                Add Component
              </Button>
            </div>
            
            <div className="space-y-3">
              {formData.components.filter(comp => comp.type === 'deduction').map((component) => (
                <div key={component.id} className="border rounded-md p-3 space-y-3">
                  <div className="flex justify-between items-center">
                    <div className="font-medium text-sm">
                      {component.name}
                      {component.isRequired && (
                        <span className="text-xs text-blue-600 ml-2">(Required)</span>
                      )}
                    </div>
                    
                    {!component.isRequired && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 w-6 p-0 text-red-500" 
                        onClick={() => removeComponent(component.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor={`${component.id}-name`} className="text-xs">Name</Label>
                      <Input
                        id={`${component.id}-name`}
                        value={component.name}
                        onChange={(e) => handleComponentChange(component.id, 'name', e.target.value)}
                        className="h-8 text-sm"
                        disabled={!component.isEditable}
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <Label htmlFor={`${component.id}-calcType`} className="text-xs">Calculation Type</Label>
                      <Select 
                        value={component.calculationType} 
                        onValueChange={(value) => handleComponentChange(component.id, 'calculationType', value as 'percentage' | 'fixed')}
                        disabled={!component.isEditable}
                      >
                        <SelectTrigger id={`${component.id}-calcType`} className="h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percentage">Percentage</SelectItem>
                          <SelectItem value="fixed">Fixed Amount</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-1">
                      <Label htmlFor={`${component.id}-value`} className="text-xs">
                        {component.calculationType === 'percentage' ? 'Percentage (%)' : 'Amount (₹)'}
                      </Label>
                      <Input
                        id={`${component.id}-value`}
                        type="number"
                        value={component.value}
                        onChange={(e) => handleComponentChange(component.id, 'value', parseFloat(e.target.value))}
                        className="h-8 text-sm"
                        disabled={!component.isEditable}
                      />
                    </div>
                    
                    {component.calculationType === 'percentage' && (
                      <div className="space-y-1">
                        <Label htmlFor={`${component.id}-baseOn`} className="text-xs">Based On</Label>
                        <Select 
                          value={component.baseOn || 'basic'} 
                          onValueChange={(value) => handleComponentChange(component.id, 'baseOn', value as 'basic' | 'gross')}
                          disabled={!component.isEditable}
                        >
                          <SelectTrigger id={`${component.id}-baseOn`} className="h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="basic">Basic Salary</SelectItem>
                            <SelectItem value="gross">Gross Salary</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="preview" className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{formData.name}</CardTitle>
                    <CardDescription>{formData.department} Department</CardDescription>
                  </div>
                  <div className="px-2 py-1 rounded bg-gray-100 text-sm">
                    CTC: ₹{formData.baseSalary.toLocaleString()}/month
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <SalaryBreakdownCard structure={formData} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        <DialogFooter className="border-t pt-4 mt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={!formData.name || !formData.department || formData.baseSalary <= 0}>
            <Save className="mr-2 h-4 w-4" />
            {isEditMode ? 'Update Structure' : 'Create Structure'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EmployeeSalary = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [structures, setStructures] = useState<SalaryStructure[]>(SALARY_STRUCTURES);
  const [selectedStructure, setSelectedStructure] = useState<SalaryStructure | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const structuresPerPage = 6;
  
  const departments = Array.from(new Set(structures.map(s => s.department))).sort();
  
  const filteredStructures = structures.filter(
    (structure) => {
      const matchesSearch = 
        structure.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        structure.department.toLowerCase().includes(searchQuery.toLowerCase()) ||
        structure.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesDepartment = !departmentFilter || structure.department === departmentFilter;
      const matchesStatus = !statusFilter || structure.status === statusFilter;
      
      return matchesSearch && matchesDepartment && matchesStatus;
    }
  );
  
  const totalPages = Math.ceil(filteredStructures.length / structuresPerPage);
  const indexOfLastStructure = currentPage * structuresPerPage;
  const indexOfFirstStructure = indexOfLastStructure - structuresPerPage;
  const currentStructures = filteredStructures.slice(indexOfFirstStructure, indexOfLastStructure);
  
  const paginate = (pageNumber: number) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };
  
  const handleEditStructure = (structure: SalaryStructure) => {
    setSelectedStructure(structure);
    setIsEditDialogOpen(true);
  };
  
  const handleSaveStructure = (structure: SalaryStructure) => {
    if (structure.id) {
      // Update existing structure
      setStructures(prevStructures => 
        prevStructures.map(s => 
          s.id === structure.id ? structure : s
        )
      );
      
      toast({
        title: "Structure Updated",
        description: `${structure.name} salary structure has been updated.`
      });
    } else {
      // Create new structure
      const newStructure = {
        ...structure,
        id: (structures.length + 1).toString(),
        createdDate: new Date().toISOString().split('T')[0],
        lastModified: new Date().toISOString().split('T')[0],
        createdBy: 'Current User',
        assignedEmployees: 0
      };
      
      setStructures(prevStructures => [...prevStructures, newStructure]);
      
      toast({
        title: "Structure Created",
        description: `${structure.name} salary structure has been created.`
      });
    }
  };
  
  const handleDeleteStructure = (id: string) => {
    setStructures(prevStructures => prevStructures.filter(s => s.id !== id));
    
    toast({
      title: "Structure Deleted",
      description: "The salary structure has been deleted."
    });
  };
  
  const handleDuplicateStructure = (structure: SalaryStructure) => {
    const newStructure: SalaryStructure = {
      ...structure,
      id: (structures.length + 1).toString(),
      name: `${structure.name} (Copy)`,
      status: 'draft',
      lastModified: new Date().toISOString().split('T')[0],
      assignedEmployees: 0
    };
    
    setStructures(prevStructures => [...prevStructures, newStructure]);
    
    toast({
      title: "Structure Duplicated",
      description: `${structure.name} has been duplicated.`
    });
  };
  
  return (
    <div className="h-full flex flex-col bg-gray-50">
      <Header title="Salary Structures" subtitle="Manage employee salary structure templates" />
      
      <div className="flex-1 p-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 sm:mb-0">Salary Structure Templates</h2>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                type="search"
                placeholder="Search templates..."
                className="pl-8 w-full"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <Select value={departmentFilter || 'All'} onValueChange={(value) => setDepartmentFilter(value || null)}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Departments</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={statusFilter || 'All'} onValueChange={(value) => setStatusFilter(value || null)}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            
            <Button onClick={() => setIsNewDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              New Structure
            </Button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {currentStructures.length > 0 ? (
            currentStructures.map((structure) => (
              <Card key={structure.id} className="shadow-sm transition-all hover:shadow">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="flex items-center gap-1">
                        {structure.name}
                        <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                          structure.status === 'active' ? 'bg-green-100 text-green-800' :
                          structure.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {structure.status.charAt(0).toUpperCase() + structure.status.slice(1)}
                        </span>
                      </CardTitle>
                      <CardDescription>{structure.department}</CardDescription>
                    </div>
                    <div className="flex space-x-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="h-7 w-7" 
                        onClick={() => handleDuplicateStructure(structure)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7"
                        onClick={() => handleEditStructure(structure)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7 text-red-500"
                        onClick={() => handleDeleteStructure(structure.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="text-sm mb-4">
                    <p className="text-gray-500 line-clamp-2">{structure.description}</p>
                  </div>
                  
                  <div className="space-y-1 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Base Salary</span>
                      <span className="font-medium">₹{structure.baseSalary.toLocaleString()}</span>
                    </div>
                    
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Components</span>
                      <span>{structure.components.length}</span>
                    </div>
                    
                    {structure.assignedEmployees !== undefined && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Assigned To</span>
                        <span>{structure.assignedEmployees} employees</span>
                      </div>
                    )}
                    
                    {structure.lastModified && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Last Updated</span>
                        <span>{structure.lastModified}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="pt-3 border-t">
                    <Button 
                      variant="outline"
                      className="w-full"
                      onClick={() => handleEditStructure(structure)}
                    >
                      <DollarSign className="mr-2 h-4 w-4" />
                      View Breakdown
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="col-span-3 py-10">
              <div className="flex flex-col items-center justify-center text-gray-500">
                <Search className="h-10 w-10 mb-2" />
                <p className="text-lg">No salary structures found</p>
                <p className="text-sm">Try adjusting your search filters</p>
              </div>
            </div>
          )}
        </div>
        
        <div className="flex justify-center mt-6">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  onClick={() => paginate(currentPage - 1)} 
                  className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum = currentPage;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <PaginationItem key={i}>
                    <PaginationLink 
                      isActive={pageNum === currentPage} 
                      onClick={() => paginate(pageNum)}
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}
              
              <PaginationItem>
                <PaginationNext 
                  onClick={() => paginate(currentPage + 1)} 
                  className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      </div>
      
      {/* Edit/Create Salary Structure Dialog */}
      <StructureEditorDialog 
        isOpen={isEditDialogOpen || isNewDialogOpen} 
        onClose={() => {
          setIsEditDialogOpen(false);
          setIsNewDialogOpen(false);
          setSelectedStructure(null);
        }}
        structure={selectedStructure}
        onSave={handleSaveStructure}
      />
    </div>
  );
};

export default EmployeeSalary;
